import { useIsReadOnly } from '@/lib/casl'
import type { MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'
import { useFieldContext } from '@/lib/form-contexts'
import { DEFAULT_UPLOAD_USAGE } from '@/lib/media-upload-profiles'
import { mergeEditedMedia, selectFirstValidAssetMedia } from '@/lib/media-editing'
import {
  getPhotoManagerConfig,
  PhotoManagerSelectionMode,
  type PhotoManagerModeValue,
  type PhotoManagerSelectionModeValue,
} from '@/lib/photo-manager-config'
import type { MediaFile } from '@/lib/services/media/media.types'
import { cn } from '@/lib/utils'
import { Camera } from 'lucide-react'
import { type ComponentProps, type ReactNode, useId, useState } from 'react'
import ImageUploader from '@/components/media/ImageUploader'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import CreateActionButton from '@/components/patterns/create-action-button'
import FieldInfo from '@/components/form/FieldInfo'

/** Uploader options a form passes on; its upload handlers replace the field's own. */
type MediaUploadInputUploaderProps = Pick<
  ComponentProps<typeof ImageUploader>,
  'defaultUploadDestination' | 'onUploadSuccess' | 'onUploadError'
>

export type MediaUploadInputProps = MediaUploadInputUploaderProps & {
  label?: ReactNode
  description?: string
  className?: string
  placeholderClassName?: string
  uploadUsage?: MediaUploadUsageValue
  photoManagerMode?: PhotoManagerModeValue
  selectionMode?: PhotoManagerSelectionModeValue
  allowFocalPoint?: boolean
  onFocalPointChange?: ((media: MediaFile) => void) | null
}

export default function MediaUploadInput({
  label,
  description,
  className,
  placeholderClassName,
  uploadUsage = DEFAULT_UPLOAD_USAGE,
  photoManagerMode,
  selectionMode = PhotoManagerSelectionMode.Single,
  allowFocalPoint = false,
  onFocalPointChange = null,
  ...props
}: MediaUploadInputProps) {
  // Undefined where the form has no entry yet, such as a favicon or a block's first photo.
  const field = useFieldContext<MediaFile | null | undefined>()
  const { isReadOnly } = useIsReadOnly()
  const id = useId()
  const uniqueUploaderId = `uppy-${field.name}-${id}`
  const [isModalOpen, setIsModalOpen] = useState(false)
  const photoManagerConfig = getPhotoManagerConfig({ mode: photoManagerMode, uploadUsage })

  // field.state.value is now the full media object { id, url, name, etc. }, not just the url
  const mediaData = field.state.value

  return (
    <div className={cn('flex flex-col gap-y-2 w-full', className)}>
      {label && <label className="text-sm font-medium">{label}</label>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {mediaData?.url ? (
            <UploadedMediaPreviewTile
              media={mediaData}
              alt={mediaData?.name || 'Uploaded image'}
              caption={mediaData?.name}
              onRemove={!isReadOnly ? () => field.setValue(null) : null}
              onEditComplete={
                !isReadOnly
                  ? (editedMedia) => field.setValue(mergeEditedMedia(mediaData, editedMedia))
                  : null
              }
              removeLabel={`Remove ${mediaData?.name || 'uploaded image'}`}
              editUploadUsage={uploadUsage}
              editAttachToPhotoManager={photoManagerConfig.attachUploadsToPhotoManager}
              allowFocalPoint={!isReadOnly && allowFocalPoint}
              onFocalPointChange={(updatedMedia) => {
                const mergedMedia = mergeEditedMedia(mediaData, updatedMedia)
                field.setValue(mergedMedia)
                onFocalPointChange?.(mergedMedia)
              }}
              wrapperClassName="w-fit"
              frameClassName={cn(
                'flex h-auto w-auto items-center justify-center overflow-hidden rounded-xl bg-muted',
                placeholderClassName
              )}
              imageClassName={cn('h-auto w-auto rounded-xl object-cover', placeholderClassName)}
            />
          ) : (
            <div
              className={cn(
                'flex h-auto w-auto items-center justify-center overflow-hidden rounded-xl bg-muted',
                placeholderClassName
              )}
            >
              <Camera className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
          <div>
            <CreateActionButton
              context="inline"
              variant="link"
              onClick={() => setIsModalOpen(true)}
              className="p-0 h-auto"
            >
              {mediaData?.url ? 'Replace Image' : 'Upload Image'}
            </CreateActionButton>
          </div>
        </div>

        {!isReadOnly && (
          <ImageUploader
            id={uniqueUploaderId}
            uploadUsage={uploadUsage}
            photoManagerMode={photoManagerMode}
            selectionMode={selectionMode}
            open={isModalOpen}
            onRequestClose={() => {
              setIsModalOpen(false)
            }}
            onUploadSuccess={(_, response) => {
              if (response?.body) {
                field.setValue(response.body)
              }
            }}
            onSelectAssets={(assets = []) => {
              const { selectedMedia, insertedCount, skippedCount } = selectFirstValidAssetMedia(
                assets,
                { uploadUsage }
              )

              if (selectedMedia) {
                field.setValue(selectedMedia)
              }

              return {
                insertedCount,
                skippedCount,
              }
            }}
            {...props}
          />
        )}
      </div>

      <FieldInfo />
    </div>
  )
}
